/**
 * What is due, what was done, and what is late.
 *
 * The deadlines are not stored anywhere. They are worked out from the statutory
 * rulepack, the year, and which duties the company carries — because a stored
 * deadline goes stale the day a Budget moves it, and the stale copy is the one
 * on the screen. What is stored is only what a person did about each one.
 *
 * Three things this page is careful about.
 *
 * **Overdue beats every other label.** A filing three weeks late that somebody
 * marked "in progress" is late, and showing it as in progress is how it stays
 * that way.
 *
 * **Interest is an estimate and says so.** The statutory computations differ in
 * their part-month conventions, and the point of the figure is "this is getting
 * expensive", not a challan amount.
 *
 * **A registration the calendar skipped gets a note, not a silent gap.** A gap
 * looks like a bug and gets "fixed" by somebody adding twelve wrong dates.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'

interface Entry {
  kind: string
  period_label: string
  period_start: string
  period_end: string
  due_date: string
  title: string
  detail: string
  location_id: string | null
  gstin: string | null
  status: string | null
  amount_paise: number | null
  paid_on: string | null
  filed_on: string | null
  reference: string | null
  reason: string | null
  record_id: string | null
  days_overdue: number
  accrued_interest_paise: number | null
  state: string
}

interface Summary {
  fy_code: string
  as_at: string
  total: number
  overdue: number
  due_soon: number
  settled: number
  accrued_interest_paise: number
  overdue_without_an_amount: number
}

interface CalendarData {
  summary: Summary
  entries: Entry[]
  notes: { gstin: string; reason: string }[]
  available_years: string[]
}

interface Duties {
  constitution: string
  pf_applicable: boolean
  esi_applicable: boolean
  pt_state: string | null
  ptrc_periodicity: string | null
  ptec_applicable: boolean
  tds_deductor: boolean
  gst_registered: boolean
  advance_tax_applicable: boolean
  msme_form1_applicable: boolean
  has_payroll: boolean
  registrations: { gstin: string; frequency: string }[]
}

const FILTERS: { key: string; label: string }[] = [
  { key: 'open', label: 'Still to do' },
  { key: 'overdue', label: 'Late' },
  { key: 'all', label: 'Everything' },
]

function currentFy(): string {
  const now = new Date()
  const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
}

function toPaise(text: string): number | null {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return null
  return Math.round(Number(clean) * 100)
}

export default function Calendar() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [fyCode, setFyCode] = useState(currentFy)
  const [filter, setFilter] = useState('open')
  const [acting, setActing] = useState<Entry | null>(null)
  const [status, setStatus] = useState('filed')
  const [on, setOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [reason, setReason] = useState('')

  const calendar = useQuery({
    queryKey: ['calendar', fyCode],
    queryFn: () => api.get<CalendarData>(`/compliance/calendar?fy_code=${fyCode}`),
  })
  const duties = useQuery({
    queryKey: ['duties'],
    queryFn: () => api.get<Duties | null>('/compliance/duties'),
  })

  const record = useMutation({
    mutationFn: (body: unknown) => api.post<Entry>('/compliance/records', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      setActing(null)
      setAmount('')
      setReference('')
      setReason('')
    },
  })

  if (calendar.isLoading) return <Loading what="Working out what is due" />
  const data = calendar.data
  const entries = (data?.entries ?? []).filter((e) => {
    if (filter === 'all') return true
    if (filter === 'overdue') return e.state === 'overdue'
    return e.state !== 'filed' && e.state !== 'not_applicable'
  })
  // Whether the company carries any duty at all. `endsWith('_applicable')`
  // alone would be true of the key regardless of its value, which would call
  // an all-off profile "owes something" and word the empty state wrongly.
  const owes =
    duties.data !== null &&
    duties.data !== undefined &&
    (duties.data.pf_applicable ||
      duties.data.esi_applicable ||
      duties.data.ptec_applicable ||
      duties.data.tds_deductor ||
      duties.data.gst_registered ||
      duties.data.advance_tax_applicable ||
      duties.data.msme_form1_applicable ||
      duties.data.pt_state !== null)

  return (
    <>
      <ErrorNote error={calendar.error ?? record.error} />

      {duties.data === null && (
        <Note kind="warn">
          No company has been set up, so nothing can be worked out about what it owes. A
          system that does not know who it is cannot know what its deadlines are — fill in
          the company profile first.
        </Note>
      )}

      {data && duties.data && data.summary.total === 0 && (
        <Note kind="warn">
          The company carries no statutory duties in its profile, so there is nothing to
          schedule. Switching one on is deliberate: raising deadlines nobody owes is how a
          calendar teaches people to ignore it.
        </Note>
      )}

      {data && data.summary.total > 0 && (
        <div className="grid">
          <Stat label="Deadlines this year" value={data.summary.total} />
          <Stat
            label="Late"
            value={data.summary.overdue}
            note={data.summary.overdue > 0 ? 'needs attention' : 'none'}
          />
          <Stat label="Due within a week" value={data.summary.due_soon} />
          <Stat
            label="Interest running"
            value={formatInr(data.summary.accrued_interest_paise)}
            note={
              data.summary.overdue_without_an_amount > 0
                ? `understated: ${data.summary.overdue_without_an_amount} late with no amount recorded`
                : 'an estimate, not a challan figure'
            }
          />
        </div>
      )}

      {(data?.notes ?? []).length > 0 && (
        <Note kind="info">
          {(data?.notes ?? []).map((n) => (
            <div key={n.gstin}>
              <strong>{n.gstin}</strong> was given no deadlines: {n.reason}.
            </div>
          ))}
        </Note>
      )}

      <Card title="Year">
        <div className="row">
          <div>
            <span className="caption">Financial year</span>
            <select value={fyCode} onChange={(e) => setFyCode(e.target.value)}>
              {(data?.available_years ?? [fyCode]).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="caption">Showing</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              {FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          Only years with a statutory rulepack can be answered for. Dates are the statutory
          ones, unadjusted — nudging one that falls on a Sunday would quietly under-report
          the risk.
        </p>
      </Card>

      <Card title="Deadlines" hint={`${entries.length}`} tight>
        {entries.length === 0 ? (
          <div className="empty">
            {owes ? 'Nothing outstanding.' : 'Nothing scheduled.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Due</th>
                <th>What</th>
                <th>Period</th>
                <th>State</th>
                <th className="num">Amount</th>
                <th className="num">Interest</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={`${entry.kind}-${entry.period_label}-${entry.location_id ?? ''}`}>
                  <td className="muted date">{entry.due_date}</td>
                  <td>
                    {entry.title}
                    {entry.gstin && <div className="faint">{entry.gstin}</div>}
                    <div className="faint">{entry.detail}</div>
                  </td>
                  <td className="muted">{entry.period_label}</td>
                  <td>
                    <Pill value={entry.state} />
                    {entry.state === 'overdue' && (
                      <div className="faint">{entry.days_overdue} days late</div>
                    )}
                    {entry.reference && <div className="faint">{entry.reference}</div>}
                    {entry.reason && <div className="faint">{entry.reason}</div>}
                  </td>
                  <td className="num muted">
                    {entry.amount_paise === null ? (
                      <span className="faint">not known</span>
                    ) : (
                      formatInr(entry.amount_paise)
                    )}
                  </td>
                  <td className="num">
                    {entry.accrued_interest_paise ? (
                      formatInr(entry.accrued_interest_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    {can('compliance.obligation.edit') && (
                      <button
                        className="small"
                        onClick={() => {
                          setActing(entry)
                          setStatus(entry.status ?? 'filed')
                          setAmount(
                            entry.amount_paise === null
                              ? ''
                              : (entry.amount_paise / 100).toString(),
                          )
                          setReference(entry.reference ?? '')
                          setReason(entry.reason ?? '')
                        }}
                      >
                        Record
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {acting && (
        <Card
          title={`${acting.title} — what was done`}
          hint={`due ${acting.due_date}`}
          actions={
            <button className="small" onClick={() => setActing(null)}>
              Cancel
            </button>
          }
        >
          <div className="row">
            <Field label="State">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="in_progress">Being prepared</option>
                <option value="paid">Paid, not yet filed</option>
                <option value="filed">Filed</option>
                <option value="not_applicable">Does not apply</option>
              </select>
            </Field>
            {(status === 'paid' || status === 'filed') && (
              <Field label={status === 'paid' ? 'Paid on' : 'Filed on'}>
                <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
              </Field>
            )}
            <Field label="Amount" caption="leave blank if not known yet">
              <input
                className="num"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
            <Field label="Reference" caption="challan, acknowledgement, ARN">
              <input value={reference} onChange={(e) => setReference(e.target.value)} />
            </Field>
          </div>
          {status === 'not_applicable' && (
            <Field label="Why not" caption="required">
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          )}
          <button
            disabled={status === 'not_applicable' && !reason.trim()}
            onClick={() =>
              record.mutate({
                kind: acting.kind,
                period_label: acting.period_label,
                location_id: acting.location_id,
                status,
                amount_paise: toPaise(amount),
                paid_on: status === 'paid' ? on : null,
                filed_on: status === 'filed' ? on : null,
                reference: reference || null,
                reason: reason || null,
                fy_code: fyCode,
              })
            }
          >
            Record it
          </button>
          <p className="faint">
            Recording the same deadline twice updates one row. Two answers to one question
            has no answer to which is right.
          </p>
        </Card>
      )}

      {duties.data && (
        <Card title="What the company owes, and why" tight>
          <table>
            <tbody>
              <tr>
                <td>Constitution</td>
                <td>{duties.data.constitution}</td>
              </tr>
              <tr>
                <td>Deducts tax at source</td>
                <td>{duties.data.tds_deductor ? 'yes' : 'no'}</td>
              </tr>
              <tr>
                <td>GST registered</td>
                <td>
                  {duties.data.gst_registered ? 'yes' : 'no'}
                  {duties.data.registrations.length === 0 ? (
                    <span className="faint">
                      {' '}
                      — but no location carries a GSTIN, so nothing is scheduled
                    </span>
                  ) : (
                    <span className="faint">
                      {' '}
                      — {duties.data.registrations.map((r) => r.gstin).join(', ')}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Pays advance tax</td>
                <td>{duties.data.advance_tax_applicable ? 'yes' : 'no'}</td>
              </tr>
              <tr>
                <td>Runs a payroll</td>
                <td>{duties.data.has_payroll ? 'yes' : 'no'}</td>
              </tr>
              <tr>
                <td>Profession tax</td>
                <td>
                  {duties.data.pt_state
                    ? `${duties.data.pt_state}, ${duties.data.ptrc_periodicity ?? 'no periodicity set'}`
                    : 'not registered'}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="faint">
            A duty switched on with no fact behind it schedules nothing — the flag says the
            business is registered, and the GSTINs on the locations say how many returns
            that actually means.
          </p>
        </Card>
      )}
    </>
  )
}
